import { RemoveIcon } from '@diligentcorp/atlas-react-icons';
import { ConfirmationModal } from '@issuemanager/apps/client/components';
import {
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  styled,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { JSX, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { useRemoveRelationship } from '../../hooks/use-issue-relationships';

// TODO: this should be a DTO
type Relationship = {
  id: string;
  code: string;
  type: string;
  title: string;
  link: string;
};

type ConfirmationModalState = {
  selectedId: string | null;
  isOpen: boolean;
};

type ConfirmationModalAction =
  | {
      type: 'OPEN_MODAL';
      payload: {
        id: string;
      };
    }
  | { type: 'CLOSE_MODAL' };

const initialState: ConfirmationModalState = {
  selectedId: null,
  isOpen: false,
};

function confirmationModalReducer(
  state: ConfirmationModalState,
  action: ConfirmationModalAction,
): ConfirmationModalState {
  switch (action.type) {
    case 'OPEN_MODAL':
      return {
        selectedId: action.payload.id,
        isOpen: true,
      };
    case 'CLOSE_MODAL':
      return {
        ...state,
        isOpen: false,
      };
    default:
      return state;
  }
}

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  marginTop: theme.spacing(2),
  backgroundColor: '#ffffff',
  borderRadius: theme.spacing(1),
  boxShadow: '0px -1px 0px 0px #E6E6E6 inset',
}));

const StyledTable = styled(Table)(({ theme }) => ({
  '& .MuiTableHead-root': {
    backgroundColor: '#EBEEF2',
    '& .MuiTableCell-root': {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  '& .MuiTableBody-root .MuiTableRow-root': {
    backgroundColor: '#ffffff',
    '&:nth-of-type(even)': {
      backgroundColor: '#EBEEF2',
    },
  },
}));

type RelationshipsTableProps = {
  relationships: Relationship[];
  code: string;
};

export function RelationshipsTable({
  relationships,
  code,
}: RelationshipsTableProps): JSX.Element {
  const { t } = useTranslation(['issue']);
  const { enqueueSnackbar } = useSnackbar();
  const removeRelationship = useRemoveRelationship(code);
  const [modalState, dispatch] = useReducer(
    confirmationModalReducer,
    initialState,
  );

  const selectedRelationship = modalState.selectedId
    ? relationships.find((r) => r.id === modalState.selectedId)
    : null;

  const handleOpenModal = (relationship: Relationship): void => {
    dispatch({
      type: 'OPEN_MODAL',
      payload: {
        id: relationship.id,
      },
    });
  };

  const handleCloseModal = (): void => {
    dispatch({ type: 'CLOSE_MODAL' });
  };

  const handleRemoveRelationship = async (id: string): Promise<void> => {
    await removeRelationship.mutateAsync(id, {
      onSuccess: () => {
        enqueueSnackbar(
          t('relationships.removeSuccess', {
            type: selectedRelationship?.type,
          }),
          {
            variant: 'success',
          },
        );
      },
      onError: () => {
        enqueueSnackbar(
          t('relationships.removeError', {
            type: selectedRelationship?.type,
          }),
          {
            variant: 'error',
          },
        );
      },
    });
  };

  const handleAccept = async (): Promise<void> => {
    if (modalState.selectedId) {
      await handleRemoveRelationship(modalState.selectedId);
      handleCloseModal();
    }
  };

  return (
    <>
      <StyledTableContainer>
        <StyledTable data-testid='relationships-table'>
          <TableHead>
            <TableRow>
              <TableCell>
                <Typography fontWeight={600}>
                  {t('issue:relationships.table.id')}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography fontWeight={600}>
                  {t('issue:relationships.table.type')}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography fontWeight={600}>
                  {t('issue:relationships.table.title')}
                </Typography>
              </TableCell>
              <TableCell align='right' sx={{ width: '50px' }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {relationships.map((relationship) => (
              <TableRow
                key={relationship.id}
                data-testid={`relationship-row-${relationship.id}`}
              >
                <TableCell>
                  <Link
                    href={relationship.link}
                    underline='hover'
                    color='primary'
                    data-testid={`relationship-id-link-${relationship.id}`}
                  >
                    {relationship.code}
                  </Link>
                </TableCell>
                <TableCell>
                  <Typography variant='body2'>{relationship.type}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='body2'>{relationship.title}</Typography>
                </TableCell>
                <TableCell align='right'>
                  <IconButton
                    onClick={() => {
                      handleOpenModal(relationship);
                    }}
                    size='small'
                    aria-label={t('issue:relationships.actions.remove')}
                    data-testid={`remove-relationship-${relationship.id}`}
                  >
                    <RemoveIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </StyledTable>
      </StyledTableContainer>
      <ConfirmationModal
        onAccept={handleAccept}
        onCancel={handleCloseModal}
        isOpen={modalState.isOpen}
        title={t('issue:relationships.confirmRemove.title')}
        description={t('issue:relationships.confirmRemove.description', {
          code: selectedRelationship?.code,
          type: selectedRelationship?.type,
          title: selectedRelationship?.title,
        })}
        swapButtons
        acceptButtonProps={{
          text: t('issue:relationships.confirmRemove.accept'),
        }}
        cancelButtonProps={{
          text: t('issue:relationships.confirmRemove.cancel'),
        }}
      />
    </>
  );
}
